/**
 * Media Recovery
 *
 * Восстановление мёртвых/зомби медиа-треков после прерываний ОС
 * (звонки/будильник/Siri) на мобильном Safari, а также при возврате
 * вкладки из фона и при освобождении занятых устройств.
 *
 * Фабрика владеет своим внутренним состоянием (needsRecovery, recovering,
 * таймеры, health monitor) и зависит от стора только через переданные
 * рефы и функции requestAccess/loadDevices.
 */

import type { Ref } from 'vue'
import type { PermissionState } from '../deviceManager'

export interface MediaRecoveryDeps {
  previewStream: Ref<MediaStream | null>
  cameraPermission: Ref<PermissionState>
  microphonePermission: Ref<PermissionState>
  deadTrackRecoveryCount: Ref<number>
  requestAccess: () => Promise<boolean>
  loadDevices: () => Promise<void>
}

export function createMediaRecovery(deps: MediaRecoveryDeps) {
  const { previewStream, cameraPermission, microphonePermission, deadTrackRecoveryCount } = deps

  let needsRecovery = false
  let recovering = false
  // Во время смены камеры previewStream временно содержит остановленный видео-трек
  // и переносимый аудио — монитор здоровья принял бы это за зомби и запустил бы
  // recovery параллельно захвату новой камеры (утечка устройств). Флаг глушит
  // монитор и visibility-recovery на время переключения.
  let switching = false
  let muteRecoveryTimer: ReturnType<typeof setTimeout> | null = null
  let healthCheckInterval: ReturnType<typeof setInterval> | null = null
  let lastRecoveryTime = 0
  let recoveryAttempt = 0

  function attachTrackEndedListeners(stream: MediaStream): void {
    stream.getTracks().forEach(track => {
      track.addEventListener('ended', () => {
        console.warn(`[deviceManager] Track ${track.kind} ended (killed by OS)`)
        needsRecovery = true
        // If page is visible, recover immediately
        if (document.visibilityState === 'visible') {
          recoverDeadTracks()
        }
        // Otherwise, recovery will happen on visibilitychange
      })

      // iOS Safari fires 'mute' when alarm/Siri/phone call interrupts audio session.
      // Track stays readyState='live' but produces no media (zombie track).
      track.addEventListener('mute', () => {
        console.warn(`[deviceManager] Track ${track.kind} muted by OS`)
        // Wait 3s — if track doesn't unmute, it's a persistent interruption
        if (muteRecoveryTimer) clearTimeout(muteRecoveryTimer)
        muteRecoveryTimer = setTimeout(() => {
          muteRecoveryTimer = null
          if (track.muted && document.visibilityState === 'visible') {
            console.warn(`[deviceManager] Track ${track.kind} still muted after 3s, recovering`)
            needsRecovery = true
            recoverDeadTracks()
          }
        }, 3000)
      })

      track.addEventListener('unmute', () => {
        // Track recovered on its own — cancel recovery timer
        if (muteRecoveryTimer) {
          clearTimeout(muteRecoveryTimer)
          muteRecoveryTimer = null
          console.info(`[deviceManager] Track ${track.kind} unmuted, recovery cancelled`)
        }
      })
    })
  }

  function startHealthMonitor(): void {
    if (healthCheckInterval) return
    healthCheckInterval = setInterval(checkTrackHealth, 2500)
  }

  function stopHealthMonitor(): void {
    if (healthCheckInterval) {
      clearInterval(healthCheckInterval)
      healthCheckInterval = null
    }
    recoveryAttempt = 0
    lastRecoveryTime = 0
  }

  function beginSwitch(): void {
    switching = true
  }

  function endSwitch(): void {
    switching = false
  }

  function checkTrackHealth(): void {
    if (!previewStream.value || recovering || switching) return

    const enabledTracks = previewStream.value.getTracks().filter(t => t.enabled)
    if (enabledTracks.length === 0) return

    const hasZombie = enabledTracks.some(t => t.muted || t.readyState !== 'live')

    if (!hasZombie) {
      recoveryAttempt = 0
      lastRecoveryTime = 0
      return
    }

    // Fixed 5s interval between recovery attempts
    const now = Date.now()
    if (lastRecoveryTime > 0 && now - lastRecoveryTime < 5000) return

    console.warn(`[deviceManager] Health check: zombie tracks detected (attempt ${recoveryAttempt + 1})`)
    lastRecoveryTime = now
    recoveryAttempt++
    recoverDeadTracks()
  }

  async function recoverDeadTracks(): Promise<void> {
    if (recovering || switching) return
    recovering = true
    needsRecovery = false

    deadTrackRecoveryCount.value++
    console.warn('[deviceManager] Recovering dead/zombie tracks')

    const oldStream = previewStream.value
    if (oldStream) {
      oldStream.getTracks().forEach(t => t.stop())
    }

    const ok = await deps.requestAccess()
    recovering = false

    if (ok) {
      console.info('[deviceManager] Stream recovered after interruption')
    } else {
      console.warn('[deviceManager] Recovery attempt failed, health monitor will retry')
    }
  }

  const onVisibilityChange = async () => {
    if (document.visibilityState !== 'visible' || switching) return

    await new Promise(r => setTimeout(r, 500))

    // Recovery for unavailable devices
    const camUnavailable = cameraPermission.value === 'unavailable'
    const micUnavailable = microphonePermission.value === 'unavailable'

    if (camUnavailable || micUnavailable) {
      let recovered = false

      if (camUnavailable) {
        try {
          const testStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
          testStream.getTracks().forEach(t => t.stop())
          cameraPermission.value = 'granted'
          recovered = true
        } catch {
          // Still busy, keep locked
        }
      }

      if (micUnavailable) {
        try {
          const testStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
          testStream.getTracks().forEach(t => t.stop())
          microphonePermission.value = 'granted'
          recovered = true
        } catch {
          // Still busy
        }
      }

      if (recovered) {
        await deps.loadDevices()
      }
    }

    // Recovery for dead tracks after alarm/phone call/OS interruption.
    // track.onended sets needsRecovery, but if it fires while backgrounded
    // we can't getUserMedia — so we also check here.
    if (previewStream.value) {
      const enabledTracks = previewStream.value.getTracks().filter(t => t.enabled)
      const hasAnyDead = enabledTracks.some(t => t.readyState !== 'live' || t.muted)
      if (needsRecovery || hasAnyDead) {
        await recoverDeadTracks()
      }
    }
  }

  // Мобильный Firefox/Safari не вызывают onUnmounted при закрытии вкладки —
  // камера и микрофон остаются захваченными. pagehide надёжно срабатывает на обоих.
  const onPageHide = () => {
    if (previewStream.value) {
      previewStream.value.getTracks().forEach(t => t.stop())
    }
  }

  function stop(): void {
    if (muteRecoveryTimer) {
      clearTimeout(muteRecoveryTimer)
      muteRecoveryTimer = null
    }
    stopHealthMonitor()
  }

  return {
    attachTrackEndedListeners,
    startHealthMonitor,
    stopHealthMonitor,
    beginSwitch,
    endSwitch,
    onVisibilityChange,
    onPageHide,
    stop
  }
}
